/**
 * Native component type definitions for whiteboard components
 * These are built-in drawing components like shapes, text, and sticky notes
 */

import type { UnifiedComponentNode } from './component.types.ts';

/**
 * Types of native components available in the whiteboard
 */
export type NativeComponentType = 'shape' | 'text' | 'sticky' | 'image' | 'csv';

/**
 * State interface for native component-specific properties
 */
export interface ComponentState {
  // Common state for all native components
  locked?: boolean;
  layer?: number;

  // Shape-specific state
  shapeType?: 'rectangle' | 'triangle' | 'square';
  fillColor?: string;
  strokeColor?: string;
  strokeWidth?: number;

  // Text-specific state
  text?: string;
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: string;
  textAlign?: 'left' | 'center' | 'right';
  textColor?: string;

  // Sticky-specific state
  stickyColor?: 'yellow' | 'pink' | 'blue' | 'green';
  padding?: number;

  // Image-specific state
  imageId?: string; // Reference to IndexedDB stored image
  blobUrl?: string; // Runtime blob URL for display (not persisted)
  alt?: string; // Alt text for accessibility
  format?: string; // 'png', 'jpeg', 'webp', etc.
  sizeKB?: number; // File size in KB
  dimensions?: {
    width: number;
    height: number;
    aspectRatio: number;
  };
  metadata?: {
    pastedAt: number;
    originalSize?: { width: number; height: number };
    compressed: boolean; // Whether image was optimized
  };

  // CSV-specific state
  csvData?: string[][];
  selectedCell?: {
    row: number;
    col: number;
  };
  readonly?: boolean;
  maxRows?: number;
  maxColumns?: number;
  showHeaders?: boolean;
}

/**
 * Native component node structure
 * Extends UnifiedComponentNode with native-specific properties
 */
export interface NativeComponentNode
  extends Omit<UnifiedComponentNode, 'originalCode' | 'compiledCode' | 'source'> {
  // Override component type to be native
  componentType: 'native';

  // Type of native component
  nativeType: NativeComponentType;

  // Component-specific state
  state: ComponentState;

  // Native components have a different source
  source: 'native';

  // Native components don't need code fields
  originalCode: '';
  compiledCode?: undefined;

  // No compilation-related fields
  compiledHash?: undefined;
  compiledAt?: undefined;
  compilerVersion?: undefined;
}

/**
 * Helper type guard to check if a node is a native component
 */
export function isNativeComponentNode(
  node: UnifiedComponentNode | NativeComponentNode,
): node is NativeComponentNode {
  return 'componentType' in node && node.componentType === 'native';
}

/**
 * Default state values for different native component types
 */
export const defaultComponentStates: Record<NativeComponentType, ComponentState> = {
  shape: {
    shapeType: 'rectangle',
    fillColor: '#ffffff',
    strokeColor: '#6366f1',
    strokeWidth: 2,
    locked: false,
    layer: 0,
  },
  text: {
    text: 'Text',
    fontSize: 16,
    fontFamily: 'Inter, system-ui, sans-serif',
    fontWeight: '400',
    textAlign: 'left',
    textColor: '#111827',
    locked: false,
    layer: 0,
  },
  sticky: {
    stickyColor: 'yellow',
    text: '',
    fontSize: 14,
    fontFamily: 'Inter, system-ui, sans-serif',
    textColor: '#111827',
    padding: 16,
    locked: false,
    layer: 0,
  },
  image: {
    alt: 'Pasted image',
    locked: false,
    layer: 0,
    metadata: {
      pastedAt: Date.now(),
      compressed: false,
    },
  },
  csv: {
    csvData: [['']],
    selectedCell: {
      row: 0,
      col: 0,
    },
    readonly: false,
    maxRows: 100,
    maxColumns: 26,
    showHeaders: true,
    locked: false,
    layer: 0,
  },
};
